import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Radio, Copy, Play, Square, ExternalLink, Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";

/*  /streamer/live?code=XXXX — streamer control panel.
    - Pick a case → start a live session
    - See real-time vote tally
    - Copy OBS overlay URL
    - 7-platform share hub with pre-written captions
    - Global leaderboard (tonight's top streamers)
*/

const StreamerLive = () => {
  const [sp] = useSearchParams();
  const code = (sp.get("code") || "").toUpperCase();

  const [cases, setCases]       = useState([]);
  const [picked, setPicked]     = useState("heist_crew");
  const [status, setStatus]     = useState(null);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding]     = useState(false);
  const [board, setBoard]       = useState(null);

  // Load cases catalog once
  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data.cases || [])).catch(() => {});
  }, []);

  // Poll live status + global leaderboard
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const [s, b] = await Promise.all([
          api.get(`/streamer/live/${code}/status`),
          api.get("/live/global-leaderboard?limit=10"),
        ]);
        if (cancelled) return;
        setStatus(s.data);
        setBoard(b.data);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 3500);
    return () => { cancelled = true; clearInterval(id); };
  }, [code]);

  const start = async () => {
    if (!picked) return;
    setStarting(true);
    try {
      await api.post("/streamer/live/start", { code, case_id: picked });
      toast.success("You're live.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't go live.");
    } finally {
      setStarting(false);
    }
  };

  const end = async () => {
    setEnding(true);
    try {
      await api.post(`/streamer/live/${code}/end`);
      toast.success("Case closed.");
    } catch {} finally {
      setEnding(false);
    }
  };

  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copied."); } catch {}
  };

  if (!code) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="text-sm text-[#8b0000] mb-2">⚠</div>
        <div className="text-sm">Missing streamer code. Open from your dashboard.</div>
        <Link to="/streamer" className="text-[#d4af37] text-xs mt-4 inline-block">← Streamer program</Link>
      </div>
    </div>
  );

  if (!status) return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#d4af37]" />
    </div>
  );

  const landing = status.landing_url || "";
  const overlay = status.overlay_url || "";

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 sm:px-6 py-8" data-testid="streamer-live-control">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to={`/streamer/dashboard?code=${code}`} className="text-[10px] tracking-[0.3em] text-[#5a5050] hover:text-[#d4af37]">
            ← dashboard
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            @{status.streamer?.handle} · {code}
          </div>
        </div>

        {/* Hero header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            {status.live ? (
              <>
                <div className="w-2.5 h-2.5 rounded-full bg-[#8b0000] dispatcher-pulse" />
                <div className="text-[10px] tracking-[0.4em] text-[#8b0000]">LIVE NOW</div>
              </>
            ) : (
              <>
                <div className="w-2.5 h-2.5 rounded-full bg-[#5a5050]" />
                <div className="text-[10px] tracking-[0.4em] text-[#5a5050]">STANDBY</div>
              </>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl text-[#f4f0ec] leading-tight">
            {status.live ? status.case.title : "Pick a case. Go live."}
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* MAIN COLUMN — case picker / live stats */}
          <div className="lg:col-span-2 space-y-6">
            {status.live ? (
              <div className="card-noir p-6" data-testid="live-stats-panel">
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="text-center">
                    <div className="font-display text-3xl text-[#d4af37]">{status.total_votes}</div>
                    <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">VOTES</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display text-3xl text-[#d4af37]">{status.viewers_count}</div>
                    <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">VIEWERS</div>
                  </div>
                  <div className="text-center">
                    <div className="font-display text-3xl text-[#d4af37]">{status.buys_count}</div>
                    <div className="text-[9px] tracking-[0.2em] text-[#5a5050]">BUYS</div>
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">CURRENT TALLY</div>
                <div className="space-y-2">
                  {(status.suspects || []).map((s, i) => (
                    <div key={s.key} className="relative overflow-hidden bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#8b0000]/20 transition-all duration-500 pointer-events-none"
                        style={{ width: `${s.percent}%` }}
                      />
                      <div className="relative flex items-center justify-between text-sm">
                        <span className="text-[#f4f0ec]">
                          <span className="text-[#d4af37] mr-2">{String(i + 1).padStart(2, "0")}</span>
                          {s.title}
                        </span>
                        <span className="text-xs text-[#a39797]">{s.votes} · {s.percent}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={end}
                  disabled={ending}
                  className="btn-ghost mt-5 text-xs"
                  data-testid="live-end-btn"
                >
                  {ending ? <Loader2 size={12} className="animate-spin" /> : <Square size={12} />}
                  End this case
                </button>
              </div>
            ) : (
              <div className="card-noir p-6" data-testid="case-picker">
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">PICK YOUR CASE</div>
                <div className="space-y-2 mb-5 max-h-96 overflow-y-auto dispatcher-scroll">
                  {cases.map((c) => (
                    <label
                      key={c.id}
                      className={`block cursor-pointer rounded-md border px-4 py-3 transition-colors ${
                        picked === c.id ? "border-[#d4af37] bg-[#d4af37]/5" : "border-[#2a2525] hover:border-[#8b0000]/60"
                      }`}
                      data-testid={`case-option-${c.id}`}
                    >
                      <input
                        type="radio" className="hidden"
                        checked={picked === c.id}
                        onChange={() => setPicked(c.id)}
                      />
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10px] tracking-[0.2em] text-[#a39797]">CASE #{c.number}</div>
                          <div className="text-sm text-[#f4f0ec] mt-0.5">{c.title}</div>
                        </div>
                        <div className="text-xs text-[#d4af37]">{c.price_label}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <button
                  onClick={start}
                  disabled={starting || !picked}
                  className="btn-primary w-full py-3"
                  data-testid="go-live-btn"
                >
                  {starting ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  {starting ? "Opening comms…" : "GO LIVE"}
                </button>
              </div>
            )}

            {/* OBS + landing URLs */}
            <div className="card-noir p-5" data-testid="stream-urls">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR BROADCAST URLS</div>
              <div className="space-y-3">
                <div>
                  <div className="text-[9px] tracking-[0.2em] text-[#a39797] mb-1.5">OBS BROWSER SOURCE (overlay)</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-[#1a1515] border border-[#2a2525] px-3 py-2 rounded-md text-[11px] text-[#f4f0ec] break-all">
                      {overlay}
                    </code>
                    <button onClick={() => copy(overlay)} className="btn-ghost text-xs">
                      <Copy size={12} />
                    </button>
                  </div>
                  <div className="text-[9px] text-[#5a5050] mt-1">Drop into OBS → + → Browser Source → paste URL → 1920×1080</div>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.2em] text-[#a39797] mb-1.5">CHAT LANDING (for viewers)</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-[#1a1515] border border-[#2a2525] px-3 py-2 rounded-md text-[11px] text-[#d4af37] break-all">
                      {landing}
                    </code>
                    <button onClick={() => copy(landing)} className="btn-ghost text-xs">
                      <Copy size={12} />
                    </button>
                    <a href={landing} target="_blank" rel="noopener" className="btn-ghost text-xs">
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* 7-platform share hub */}
            {status.captions?.length > 0 && (
              <div className="card-noir p-5" data-testid="share-hub">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={13} className="text-[#d4af37]" />
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
                    SHARE TO YOUR PLATFORMS
                  </div>
                </div>
                <div className="space-y-2">
                  {status.captions.map((c) => (
                    <div
                      key={c.platform}
                      className="bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5"
                      data-testid={`share-${c.platform}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div>
                          <span className="text-sm text-[#f4f0ec]">{c.label}</span>
                          <span className="text-[9px] text-[#5a5050] ml-2">· {c.channel}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {c.href && (
                            <a href={c.href} target="_blank" rel="noopener" className="btn-ghost text-[10px]">
                              <ExternalLink size={10} /> Share
                            </a>
                          )}
                          <button onClick={() => copy(c.text)} className="btn-ghost text-[10px]">
                            <Copy size={10} /> Copy
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-[#a39797] leading-relaxed line-clamp-2">
                        {c.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SIDE COLUMN — nightly global leaderboard */}
          <div className="lg:col-span-1">
            <div className="card-noir p-5 sticky top-6" data-testid="global-leaderboard">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={13} className="text-[#d4af37]" />
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
                  TONIGHT'S GLOBAL BOARD
                </div>
              </div>
              {!board?.rows?.length ? (
                <div className="text-xs text-[#5a5050] text-center py-6">
                  No live cases yet tonight. Be the first.
                </div>
              ) : (
                <div className="space-y-2">
                  {board.rows.map((r, i) => (
                    <div
                      key={r.code}
                      className={`flex items-center justify-between px-3 py-2 rounded-md ${
                        r.code === code ? "bg-[#d4af37]/10 border border-[#d4af37]/40" : "bg-[#1a1515]"
                      }`}
                      data-testid={`board-row-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] text-[#d4af37] w-5 flex-shrink-0">
                          #{String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0">
                          <div className="text-xs text-[#f4f0ec] truncate">@{r.handle}</div>
                          <div className="text-[9px] text-[#5a5050]">
                            {r.platform} · {r.sessions} case{r.sessions !== 1 ? "s" : ""}
                          </div>
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <div className="text-xs text-[#d4af37]">{r.score}</div>
                        <div className="text-[9px] text-[#5a5050]">
                          {r.votes}v · {r.buys}b
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[9px] text-[#5a5050] mt-4 leading-relaxed">
                Score = votes + viewers×2 + buys×25. Resets at 00:00 UTC. Live cases don't fight — this is TONIGHT'S overall participation.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamerLive;
